import React from "react";

export default function OitSummaryReport(props) {
  var report = props.report || {};
  var className = props.className;
  var meta = report.meta || {};
  var people = report.people || {};
  var programStatus = report.programStatus || {};
  var executiveOverview = report.executiveOverview || {};
  var performanceTrend = report.performanceTrend || {};
  var performanceExceptions = report.performanceExceptions || {};
  var criticalExposure = report.criticalExposure || {};
  var officerSafetyExposure = report.officerSafetyExposure || [];
  var riskIndicators = report.riskIndicators || [];
  var commandStaffNotes = report.commandStaffNotes || report.chiefNotes || [];
  var nextPhaseFocus = report.nextPhaseFocus || {};
  var overallAssessment = report.overallAssessment || {};
  var callLogsTracked = report.callLogsTracked !== false;
  var callLogsNotTrackedMessage = "Not applicable because call logs are not tracked.";
  var programStatusSymbol = programStatus.tone === "risk" ? "!" : programStatus.tone === "positive" ? "✓" : "?";

  function fmtNum(value, digits) {
    var num = Number(value);
    if (value === null || value === undefined || isNaN(num)) return "N/A";
    return num.toFixed(digits == null ? 2 : digits);
  }

  function fmtDelta(value) {
    var num = Number(value);
    if (value === null || value === undefined || isNaN(num)) return "N/A";
    return (num >= 0 ? "+" : "") + num.toFixed(2);
  }

  function fmtDate(value) {
    if (!value) return "N/A";
    var date = new Date(value);
    if (isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString("en-US", {month: "short", day: "numeric", year: "numeric"});
  }

  function toneColor(tone) {
    if (tone === "risk") return {bg: "#C91E1E", fg: "#FFFFFF", light: "#FDECEC"};
    if (tone === "attention") return {bg: "#E66A00", fg: "#FFFFFF", light: "#FFF3E8"};
    if (tone === "positive") return {bg: "#2F7D3D", fg: "#FFFFFF", light: "#EAF6ED"};
    return {bg: "#1F66B3", fg: "#FFFFFF", light: "#EEF5FF"};
  }

  function Card(p) {
    return (
      <section
        className="oit-summary-report-card"
        style={{
          background: "#FFFFFF",
          border: "1px solid #CFD8E3",
          borderRadius: 8,
          padding: 14,
          boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)",
          minHeight: 0,
          overflow: "hidden"
        }}
      >
        <div
          style={{
            color: "#061E4A",
            fontWeight: 900,
            fontSize: 15,
            textTransform: "uppercase",
            borderBottom: "1px solid #C8D3E0",
            paddingBottom: 8,
            marginBottom: 10,
            display: "flex",
            justifyContent: "space-between",
            gap: 10,
            alignItems: "center"
          }}
        >
          <span>{p.title}</span>
          {p.right || null}
        </div>
        {p.children}
      </section>
    );
  }

  function MiniTable(p) {
    var rows = p.rows || [];
    return (
      <div className="oit-summary-table-scroll">
        <div className="oit-summary-table" style={{minWidth: p.minWidth || 320, borderColor: "#D8E0EA"}}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: p.columns,
              background: p.headerBg || "#F4F7FB",
              color: p.headerColor || "#061E4A",
              fontSize: 11,
              fontWeight: 900,
              textTransform: "uppercase"
            }}
          >
            {(p.headers || []).map(function (header) {
              return <div key={header} style={{padding: "7px 8px", borderRight: "1px solid #D8E0EA"}}>{header}</div>;
            })}
          </div>
          {rows.length ? rows.map(function (row, rowIndex) {
            return (
              <div
                key={rowIndex}
                style={{
                  display: "grid",
                  gridTemplateColumns: p.columns,
                  fontSize: 12,
                  fontWeight: 700,
                  borderTop: "1px solid #E6ECF3",
                  minHeight: 28,
                  alignItems: "center"
                }}
              >
                {p.render(row).map(function (cell, cellIndex) {
                  return (
                    <div key={cellIndex} style={{padding: "6px 8px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal"}}>
                      {cell}
                    </div>
                  );
                })}
              </div>
            );
          }) : (
            <div style={{padding: "9px 8px", fontSize: 12, fontWeight: 700, color: "#667085"}}>No data available.</div>
          )}
        </div>
      </div>
    );
  }

  function BulletList(p) {
    var items = (p.items || []).filter(function (item) { return !!item; });
    if (!items.length) {
      return <div style={{fontSize: 12, color: "#667085", fontWeight: 700}}>No items recorded.</div>;
    }
    return (
      <ul style={{margin: 0, paddingLeft: 17}}>
        {items.slice(0, p.limit || 5).map(function (item, index) {
          return <li key={index} style={{fontSize: 12, lineHeight: 1.45, marginBottom: 4, fontWeight: 700}}>{String(item)}</li>;
        })}
      </ul>
    );
  }

  function OverviewColumn(p) {
    var colors = toneColor(p.tone);
    return (
      <div className="oit-summary-overview-column" style={{borderRight: p.last ? "none" : "1px solid #D8E0EA", padding: "4px 12px", minWidth: 0}}>
        <div style={{display: "flex", alignItems: "center", gap: 8, marginBottom: 8}}>
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 20,
              background: colors.bg,
              color: colors.fg,
              display: "grid",
              placeItems: "center",
              fontWeight: 900,
              fontSize: 18
            }}
          >
            {p.symbol}
          </div>
          <div style={{fontSize: 12, fontWeight: 900, color: colors.bg, textTransform: "uppercase"}}>{p.title}</div>
        </div>
        <BulletList items={p.items} limit={4}/>
      </div>
    );
  }

  function StatusBadge(p) {
    var colors = toneColor(p.tone);
    return (
      <span
        style={{
          display: "inline-block",
          background: colors.bg,
          color: colors.fg,
          borderRadius: 5,
          padding: "3px 10px",
          fontSize: 11,
          fontWeight: 900,
          textTransform: "uppercase"
        }}
      >
        {p.children}
      </span>
    );
  }

  function TrendChart() {
    var phases = (performanceTrend.phases || []).filter(function (row) {
      return row.userScore !== null && row.userScore !== undefined || row.departmentScore !== null && row.departmentScore !== undefined;
    });
    if (!phases.length && performanceTrend.overallScore !== null && performanceTrend.overallScore !== undefined) {
      phases = [{phase: performanceTrend.currentPhaseName || "Current", userScore: performanceTrend.overallScore, departmentScore: performanceTrend.departmentScore}];
    }
    function scoreY(score) {
      return 130 - ((Number(score) - 1) / 4 * 105);
    }
    let points = phases.filter(function (row) {
      return row.userScore !== null && row.userScore !== undefined;
    }).map(function (row, index) {
      let phaseIndex = phases.indexOf(row);
      let x = phases.length === 1 ? 70 : 38 + (phaseIndex * (310 / Math.max(phases.length - 1, 1)));
      let y = scoreY(row.userScore);
      return {x: x, y: y, row: row};
    });
    let departmentPoints = phases.filter(function (row) {
      return row.departmentScore !== null && row.departmentScore !== undefined;
    }).map(function (row) {
      let phaseIndex = phases.indexOf(row);
      let x = phases.length === 1 ? 70 : 38 + (phaseIndex * (310 / Math.max(phases.length - 1, 1)));
      let y = scoreY(row.departmentScore);
      return {x: x, y: y, row: row};
    });
    let polyline = points.map(function (point) { return point.x + "," + point.y; }).join(" ");
    let departmentPolyline = departmentPoints.map(function (point) { return point.x + "," + point.y; }).join(" ");

    return (
      <div>
        <div className="oit-summary-metric-grid" style={{border: "1px solid #CFD8E3", borderRadius: 6, overflow: "hidden", marginBottom: 10}}>
          <Metric label="Overall Score" value={fmtNum(performanceTrend.overallScore, 2)} note={fmtDelta(performanceTrend.overallDelta)}/>
          <Metric label="Department Avg" value={fmtNum(performanceTrend.departmentScore, 2)} note="Current phase"/>
          <Metric label="Dept Gap" value={fmtDelta(performanceTrend.departmentGap)} note="Trainee vs dept"/>
        </div>
        <svg viewBox="0 0 390 160" style={{width: "100%", height: 150, display: "block"}}>
          {[1, 2, 3, 4, 5].map(function (tick) {
            var y = 130 - ((tick - 1) / 4 * 105);
            return (
              <g key={tick}>
                <line x1="32" x2="370" y1={y} y2={y} stroke="#E1E7EF" />
                <text x="8" y={y + 4} fontSize="10" fill="#1F2937">{tick.toFixed(1)}</text>
              </g>
            );
          })}
          <line x1="32" x2="370" y1="130" y2="130" stroke="#8C9AAD" />
          <line x1="32" x2="32" y1="25" y2="130" stroke="#8C9AAD" />
          {departmentPoints.length > 1 ? <polyline points={departmentPolyline} fill="none" stroke="#8C9AAD" strokeWidth="2" strokeDasharray="5 4" /> : null}
          {points.length > 1 ? <polyline points={polyline} fill="none" stroke="#2F7D3D" strokeWidth="3" /> : null}
          {departmentPoints.map(function (point, index) {
            return (
              <g key={"dept-" + index}>
                <circle cx={point.x} cy={point.y} r="4" fill="#FFFFFF" stroke="#8C9AAD" strokeWidth="2" />
                {!points[index] ? <text x={point.x - 12} y={point.y - 10} fontSize="11" fill="#111827" fontWeight="700">{fmtNum(point.row.departmentScore, 2)}</text>: null}
                {!points[index] ? <text x={point.x - 24} y="148" fontSize="10" fill="#111827" textAnchor="end" transform={"rotate(-10 " + (point.x - 44) + " 0)"}>{point.row.phase}</text>: null}
              </g>
            );
          })}
          {points.map(function (point, index) {
            return (
              <g key={index}>
                <circle cx={point.x} cy={point.y} r="5" fill="#2F7D3D" />
                <text x={point.x - 12} y={point.y - 10} fontSize="11" fill="#111827" fontWeight="700">{fmtNum(point.row.userScore, 2)}</text>
                <text x={point.x - 24} y="148" fontSize="10" fill="#111827" textAnchor="end" transform={"rotate(-10 " + (point.x - 44) + " 0)"}>{point.row.phase}</text>
              </g>
            );
          })}
        </svg>
        <div style={{display: "flex", justifyContent: "center", gap: 18, fontSize: 10, fontWeight: 900, color: "#475467", marginTop: '5px'}}>
          <span><span style={{display: "inline-block", width: 18, height: 3, background: "#2F7D3D", verticalAlign: "middle", marginRight: 5}} />Trainee</span>
          <span><span style={{display: "inline-block", width: 18, borderTop: "2px dashed #8C9AAD", verticalAlign: "middle", marginRight: 5}} />Department Avg</span>
        </div>
      </div>
    );
  }

  function Metric(p) {
    return (
      <div style={{textAlign: "center", padding: "8px 6px", borderRight: "1px solid #CFD8E3"}}>
        <div style={{fontSize: 10, color: "#061E4A", fontWeight: 900, textTransform: "uppercase"}}>{p.label}</div>
        <div style={{fontSize: 22, fontWeight: 900, color: "#111827", marginTop: 3}}>{p.value}</div>
        <div style={{fontSize: 10, color: p.note && String(p.note).charAt(0) === "+" ? "#2F7D3D" : "#667085", fontWeight: 800}}>{p.note}</div>
      </div>
    );
  }

  function statusForExposure(status) {
    if (status === "YES") return "positive";
    if (status === "LIMITED") return "attention";
    return "risk";
  }

  function iconForExposure(event) {
    var label = String(event || "").toLowerCase();
    if (label.indexOf("vehicle") > -1 || label.indexOf("stop") > -1) return "fa-car";
    if (label.indexOf("foot pursuit") > -1 || label.indexOf("pursuit") > -1) return "fa-male";
    if (label.indexOf("physical") > -1 || label.indexOf("resistance") > -1) return "fa-hand-paper-o";
    if (label.indexOf("building") > -1 || label.indexOf("search") > -1) return "fa-search";
    if (label.indexOf("domestic") > -1) return "fa-users";
    if (label.indexOf("armed") > -1) return "fa-exclamation-triangle";
    return "fa-shield";
  }

  var phaseText = people.phaseNumber && people.phaseTotal ? people.phaseNumber + " of " + people.phaseTotal : people.currentPhase || "N/A";
  var phaseDates = fmtDate(people.phaseOpenedAt || meta.asOf) + " - " + fmtDate(people.phaseClosedAt);
  var overviewColumns = [
    {title: "Strengths", tone: "positive", symbol: "+", items: executiveOverview.strengths},
    {title: "Improvements", tone: "attention", symbol: "^", items: executiveOverview.improvements},
    {title: "Weaknesses", tone: "risk", symbol: "!", items: executiveOverview.weaknesses},
    {title: "Concerns", tone: "attention", symbol: "!", items: executiveOverview.concerns},
    {title: "Focus Areas", tone: "suggestion", symbol: "o", items: executiveOverview.focusAreas, last: true}
  ];

  return (
    <div
      className={["oit-summary-report", className].filter(Boolean).join(" ")}
      style={{
        fontFamily: 'Arial, "Helvetica Neue", Helvetica, sans-serif',
        background: "#F3F6FA",
        color: "#0B1220",
        padding: 8,
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden"
      }}
    >
      <style>
        {`
          .oit-summary-report {
            box-sizing: border-box;
          }

          .oit-summary-report *,
          .oit-summary-report *::before,
          .oit-summary-report *::after {
            box-sizing: border-box;
          }

          .oit-summary-report-card,
          .oit-summary-header,
          .oit-summary-report footer {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          .oit-summary-header {
            background: linear-gradient(90deg, #061E4A 0%, #112E5F 100%);
            color: #FFFFFF;
            border-radius: 8px;
            padding: 12px 18px;
            display: grid;
            grid-template-columns: auto minmax(220px, 1fr) auto;
            gap: 18px;
            align-items: center;
            margin-bottom: 10px;
          }

          .oit-summary-title {
            font-size: 25px;
            line-height: 1.1;
            font-weight: 900;
            text-transform: uppercase;
          }

          .oit-summary-subtitle {
            font-size: 17px;
            font-weight: 800;
            margin-top: 6px;
            text-transform: uppercase;
          }

          .oit-summary-meta-grid {
            display: grid;
            grid-template-columns: repeat(5, auto);
          }

          .oit-summary-meta-item {
            border-left: 1px solid rgba(255,255,255,0.25);
            padding: 0 20px;
            min-width: 0;
          }

          .oit-summary-meta-value {
            font-size: 15px;
            font-weight: 900;
            margin-top: 7px;
            white-space: nowrap;
          }

          .oit-summary-top-grid {
            display: grid;
            grid-template-columns: minmax(240px, 280px) minmax(420px, 1fr) minmax(360px, 520px);
            gap: 10px;
            margin-bottom: 10px;
          }

          .oit-summary-two-grid {
            display: grid;
            grid-template-columns: minmax(0, 1.25fr) minmax(340px, 1fr);
            gap: 10px;
            margin-bottom: 10px;
          }

          .oit-summary-three-grid {
            display: grid;
            grid-template-columns: minmax(0, 1.2fr) minmax(280px, .85fr) minmax(300px, 1fr);
            gap: 10px;
            margin-bottom: 10px;
          }

          .oit-summary-exceptions-grid {
            display: grid;
            grid-template-columns: repeat(3, minmax(220px, 1fr));
            gap: 10px;
          }

          .oit-summary-exposure-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(220px, 1fr));
            gap: 10px;
          }

          .oit-summary-metric-grid {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }

          .oit-summary-overview-grid {
            display: grid;
            grid-template-columns: repeat(5, minmax(130px, 1fr));
            gap: 0;
            min-height: 190px;
          }

          .oit-summary-safety-grid {
            display: grid;
            grid-template-columns: repeat(8, minmax(70px, 1fr));
            gap: 8px;
          }

          .oit-summary-safety-legend {
            display: flex;
            justify-content: center;
            gap: 34px;
            margin-top: 12px;
            font-size: 11px;
            font-weight: 900;
            flex-wrap: wrap;
          }

          .oit-summary-next-grid {
            display: grid;
            grid-template-columns: minmax(190px, 1fr) minmax(210px, 1.1fr) minmax(230px, 1.15fr) minmax(180px, 220px);
            gap: 14px;
          }

          .oit-summary-table-scroll {
            overflow-x: auto;
            border: 1px solid #D8E0EA;
            border-radius: 6px;
            -webkit-overflow-scrolling: touch;
          }

          .oit-summary-table {
            overflow: hidden;
          }

          @media (max-width: 2500px) {
            .oit-summary-header {
              grid-template-columns: auto minmax(220px, 1fr);
            }

            .oit-summary-meta-grid {
              grid-column: 1 / -1;
              grid-template-columns: repeat(5, minmax(130px, 1fr));
              border-top: 1px solid rgba(255,255,255,0.18);
              padding-top: 10px;
            }

            .oit-summary-meta-item:first-child {
              border-left: none;
            }

            .oit-summary-top-grid,
            .oit-summary-two-grid,
            .oit-summary-three-grid {
              grid-template-columns: 1fr;
            }

            .oit-summary-exceptions-grid {
              grid-template-columns: repeat(3, minmax(0, 1fr));
            }

            .oit-summary-safety-grid {
              grid-template-columns: repeat(4, minmax(100px, 1fr));
            }
          }



          @media (max-width: 560px) {
            .oit-summary-title {
              font-size: 18px;
            }

            .oit-summary-meta-grid {
              grid-template-columns: 1fr;
            }

            .oit-summary-safety-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }

            .oit-summary-safety-legend {
              display: grid;
              grid-template-columns: 1fr;
              gap: 6px;
              text-align: center;
            }

            .oit-summary-report footer {
              flex-direction: column;
              gap: 4px;
              padding: 0 4px !important;
            }
          }
        `}
      </style>
      <header
        className="oit-summary-header"
      >
        <div style={{width: 58, height: 58, display: "grid", placeItems: "center"}}>
          {meta.agencyLogo ? (
            <img alt="" src={meta.agencyLogo} style={{maxWidth: 58, maxHeight: 58, objectFit: "contain"}} />
          ) : (
            <div style={{border: "2px solid #F5B43B", borderRadius: 8, padding: 8, color: "#F5B43B", fontWeight: 900}}>PD</div>
          )}
        </div>
        <div>
          <div className="oit-summary-title">FTO / PTO Phase Assessment Report</div>
          <div className="oit-summary-subtitle">{people.currentPhase || "Phase Summary"}</div>
        </div>
        <div className="oit-summary-meta-grid">
          {[
            ["Trainee", people.traineeName || "N/A"],
            ["Badge / ID", people.badgeId || "N/A"],
            ["Program", meta.programName || "N/A"],
            ["Phase", phaseText],
            ["Phase Dates", phaseDates]
          ].map(function (item) {
            return (
              <div key={item[0]} className="oit-summary-meta-item">
                <div style={{fontSize: 11, fontWeight: 900, textTransform: "uppercase", opacity: 0.88}}>{item[0]}</div>
                <div className="oit-summary-meta-value">{item[1]}</div>
              </div>
            );
          })}
        </div>
      </header>

      <div className="oit-summary-top-grid">
        <Card title="1. Program Status Estimate">
          <div style={{display: "grid", gridTemplateColumns: "76px 1fr", gap: 14, alignItems: "center", minHeight: 190}}>
            <div style={{fontSize: 68, color: toneColor(programStatus.tone).bg, fontWeight: 900, textAlign: "center"}}>{programStatusSymbol}</div>
            <div>
              <div style={{fontSize: 18, fontWeight: 900, color: toneColor(programStatus.tone).bg, textTransform: "uppercase", marginBottom: 8}}>
                {programStatus.label || "Insufficient Data"}
              </div>
              <div style={{fontSize: 12, lineHeight: 1.5, fontWeight: 700}}>
                {programStatus.summary || "There is not enough information to estimate program progress."}
              </div>
              {programStatus.confidenceNote && (
                <div style={{fontSize: 10, lineHeight: 1.4, color: "#667085", fontWeight: 700, marginTop: 8}}>
                  {programStatus.confidenceNote}
                </div>
              )}
            </div>
          </div>
        </Card>

        <Card title="2. Executive Overview (AI Generated)">
          <div className="oit-summary-overview-grid">
            {overviewColumns.map(function (column) {
              return <OverviewColumn key={column.title} {...column} />;
            })}
          </div>
        </Card>

        <Card title="3. Performance Trend (Summary)">
          <TrendChart />
        </Card>
      </div>

      <div className="oit-summary-two-grid">
        <Card title="4. Performance Exceptions">
          <div className="oit-summary-exceptions-grid">
            <MiniTable
              rows={performanceExceptions.declining}
              minWidth={420}
              columns="1.4fr .55fr .55fr .55fr"
              headers={["Declining Observations", "Prev", "Current", "Change"]}
              render={function (row) {
                return [row.observation, fmtNum(row.previous, 2), fmtNum(row.current, 2), <span style={{color: "#C91E1E"}}>{fmtDelta(row.change)}</span>];
              }}
            />
            <MiniTable
              rows={performanceExceptions.belowDepartmentAverage}
              minWidth={360}
              columns="1.5fr .65fr .65fr .55fr"
              headers={["Below Department Average", "Trainee", "Dept Avg", "Gap"]}
              render={function (row) {
                return [row.observation, fmtNum(row.trainee || row.current, 2), fmtNum(row.departmentAvg, 2), <span style={{color: Number(row.gap) < 0 ? "#C91E1E" : "#2F7D3D"}}>{fmtDelta(row.gap)}</span>];
              }}
            />
            <MiniTable
              rows={performanceExceptions.topImprovements}
              minWidth={420}
              columns="1.4fr .55fr .55fr .55fr"
              headers={["Top Improvements", "Prev", "Current", "Change"]}
              render={function (row) {
                return [row.observation, fmtNum(row.previous, 2), fmtNum(row.current, 2), <span style={{color: "#2F7D3D"}}>{fmtDelta(row.change)}</span>];
              }}
            />
          </div>
          <div style={{fontSize: 10, color: "#475467", fontWeight: 700, marginTop: 8, textAlign: "center"}}>
            Scores based on 5.0 scale. Department average appears when available from analytics.
          </div>
        </Card>

        <Card title="5. Critical Call Exposure (Recap)">
          {!callLogsTracked ? (
            <div style={{padding: 18, fontSize: 13, color: "#667085", fontWeight: 700, textAlign: "center"}}>
              {callLogsNotTrackedMessage}
            </div>
          ) : <div className="oit-summary-exposure-grid">
            <MiniTable
              rows={criticalExposure.rows}
              minWidth={320}
              columns="1fr 80px"
              headers={["Key Exposures This Phase", "Count"]}
              headerBg="#061E4A"
              headerColor="#FFFFFF"
              render={function (row) {
                return [row.callType, <span style={{color: row.count === 0 ? "#C91E1E" : "#111827"}}>{row.count}</span>];
              }}
            />
            <div>
              <div style={{background: "#C91E1E", color: "#FFFFFF", fontSize: 11, fontWeight: 900, textAlign: "center", padding: 7, borderRadius: "6px 6px 0 0", textTransform: "uppercase"}}>
                Critical Exposure Gaps
              </div>
              <div style={{border: "1px solid #D8E0EA", borderTop: "none", borderRadius: "0 0 6px 6px", padding: 12, minHeight: 116}}>
                <div style={{fontSize: 12, fontWeight: 700, marginBottom: 8}}>No exposure recorded during this phase:</div>
                <BulletList items={criticalExposure.gaps} limit={6}/>
              </div>
              <div style={{border: "1px solid #D8E0EA", borderRadius: 6, padding: 10, marginTop: 8, background: "#F7FAFE"}}>
                <div style={{fontSize: 11, fontWeight: 900, color: "#061E4A", textTransform: "uppercase", marginBottom: 6}}>Notes</div>
                <div style={{fontSize: 12, lineHeight: 1.4, fontWeight: 700}}>
                  Limited exposure categories should be considered for scenario-based training or directed call exposure.
                </div>
              </div>
            </div>
          </div>}
        </Card>
      </div>

      <div className="oit-summary-three-grid">
        <Card title="6. Officer Safety Exposure">
          {!callLogsTracked ? (
            <div style={{padding: 18, fontSize: 13, color: "#667085", fontWeight: 700, textAlign: "center"}}>
              {callLogsNotTrackedMessage}
            </div>
          ) : <React.Fragment>
          <div className="oit-summary-safety-grid">
            {officerSafetyExposure.map(function (row) {
              var iconClass = iconForExposure(row.event);
              return (
                <div key={row.event} style={{textAlign: "center"}}>
                  <div style={{fontSize: 10, fontWeight: 900, minHeight: 34}}>{row.event}</div>
                  <div style={{fontSize: 24, color: "#061E4A", lineHeight: 1.2, minHeight: 30}}>
                    <i className={"fa " + iconClass} aria-hidden="true" />
                  </div>
                  <StatusBadge tone={statusForExposure(row.status)}>{row.status}</StatusBadge>
                </div>
              );
            })}
          </div>
          <div className="oit-summary-safety-legend">
            <span style={{color: "#2F7D3D"}}>YES = Adequate Exposure</span>
            <span style={{color: "#E66A00"}}>LIMITED = Limited Exposure</span>
            <span style={{color: "#C91E1E"}}>NO = No Exposure</span>
          </div>
          </React.Fragment>}
        </Card>

        <Card title="7. Risk Indicators (AI Generated)">
          <div style={{display: "grid", gap: 8}}>
            {riskIndicators.length ? riskIndicators.slice(0, 6).map(function (row, index) {
              var isPositive = row.type === "positive";
              return (
                <div key={index} style={{display: "grid", gridTemplateColumns: "22px 1fr", gap: 8, alignItems: "start"}}>
                  <div style={{color: isPositive ? "#2F7D3D" : "#C91E1E", fontWeight: 900, fontSize: 16}}>{isPositive ? "✓" : "!"}</div>
                  <div style={{fontSize: 12, fontWeight: 700, lineHeight: 1.4}}>{row.text}</div>
                </div>
              );
            }) : <div style={{fontSize: 12, color: "#667085", fontWeight: 700}}>No risk indicators generated.</div>}
          </div>
        </Card>

        <Card title="8. Command Staff Notes">
          <div style={{display: "grid", gap: 8}}>
            {commandStaffNotes.length ? commandStaffNotes.map(function (note, index) {
              var colors = toneColor(note.type);
              return (
                <div
                  key={(note.category || note.type || "note") + "-" + index}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "34px 1fr",
                    gap: 10,
                    alignItems: "start",
                    background: colors.light,
                    border: "1px solid #D8E0EA",
                    borderRadius: 6,
                    padding: 9
                  }}
                >
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 14,
                      background: colors.bg,
                      color: colors.fg,
                      display: "grid",
                      placeItems: "center",
                      fontWeight: 900,
                      fontSize: 13,
                      textTransform: "uppercase"
                    }}
                  >
                    {String(note.type || "n").charAt(0)}
                  </div>
                  <div>
                    <div style={{fontSize: 11, color: colors.bg, fontWeight: 900, textTransform: "uppercase", marginBottom: 3}}>
                      {note.category || note.type || "Command note"}
                    </div>
                    <div style={{fontSize: 12, lineHeight: 1.45, fontWeight: 700}}>
                      {note.text}
                    </div>
                  </div>
                </div>
              );
            }) : (
              <div style={{fontSize: 12, color: "#667085", fontWeight: 700}}>
                {overallAssessment.summary || "No command staff notes generated."}
              </div>
            )}
          </div>
        </Card>
      </div>

      <Card title="9. Next Phase Focus & Recommendations">
        <div className="oit-summary-next-grid">
          <div style={{background: "#F7FAFE", borderRadius: 6, padding: 10}}>
            <div style={{fontSize: 12, fontWeight: 900, color: "#061E4A", textAlign: "center", marginBottom: 8, textTransform: "uppercase"}}>Top Development Priorities</div>
            {(nextPhaseFocus.developmentPriorities || []).slice(0, 3).map(function (item, index) {
              return (
                <div key={index} style={{display: "grid", gridTemplateColumns: "24px 1fr", gap: 8, marginBottom: 8, alignItems: "center"}}>
                  <div style={{width: 18, height: 18, borderRadius: 9, background: "#C91E1E", color: "#FFFFFF", fontSize: 11, display: "grid", placeItems: "center", fontWeight: 900}}>{index + 1}</div>
                  <div style={{fontSize: 12, fontWeight: 700}}>{item}</div>
                </div>
              );
            })}
          </div>
          <div style={{background: "#F7FAFE", borderRadius: 6, padding: 10}}>
            <div style={{fontSize: 12, fontWeight: 900, color: "#061E4A", textAlign: "center", marginBottom: 8, textTransform: "uppercase"}}>Recommended Exposure Goals</div>
            {!callLogsTracked ? (
              <div style={{fontSize: 12, color: "#667085", fontWeight: 700, textAlign: "center"}}>
                {callLogsNotTrackedMessage}
              </div>
            ) : (nextPhaseFocus.exposureGoals || []).slice(0, 5).map(function (item, index) {
              return (
                <div key={index} style={{display: "flex", justifyContent: "space-between", gap: 8, fontSize: 12, fontWeight: 800, marginBottom: 5}}>
                  <span>{item.name}</span>
                  <span>Target: {item.target}</span>
                </div>
              );
            })}
          </div>
          <div style={{background: "#F7FAFE", borderRadius: 6, padding: 10}}>
            <div style={{fontSize: 12, fontWeight: 900, color: "#061E4A", textAlign: "center", marginBottom: 8, textTransform: "uppercase"}}>Recommended Training / Remediation</div>
            <BulletList items={nextPhaseFocus.trainingRemediation} limit={5}/>
          </div>
          <div style={{border: "1px solid #A9C2E8", borderRadius: 6, padding: 14, background: "#FFFFFF"}}>
            <div style={{fontSize: 15, fontWeight: 900, color: "#061E4A", textTransform: "uppercase", marginBottom: 10}}>Remember</div>
            <div style={{fontSize: 13, lineHeight: 1.4, fontWeight: 800}}>Quality over quantity. Focus on deliberate exposure and reinforcement of critical skills.</div>
          </div>
        </div>
      </Card>

      <footer style={{display: "flex", justifyContent: "space-between", marginTop: 10, padding: "0 16px", color: "#475467", fontSize: 11, fontWeight: 700}}>
        <span>Generated by Departmentware AI</span>
        <span>Report Generated: {fmtDate(new Date())}</span>
      </footer>
    </div>
  );
}
